import React, { useMemo } from 'react';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { calculateStockIntegrityScore } from '@/utils/stockIntelligence';
import { MissingDataExplanation } from '@/components/MissingDataExplanation';

/**
 * Stock Integrity Score Indicator
 * 
 * A lightweight summary indicator that displays stock count reliability
 * based on existing analytical signals. Does not modify or override
 * any existing analysis logic.
 * 
 * Props:
 * - snapshotFcrStrength: 'Weak' | 'Moderate' | 'Strong' | null
 * - growthModelStrength: 'Weak' | 'Moderate' | 'Strong' | null
 * - spreadsheetPersistence: number (consecutive deviation samples)
 * - sampleReliabilityScore: number (0-100)
 * - showDetails: boolean (show breakdown details)
 * - compact: boolean (use compact layout)
 */
export const StockIntegrityScore = ({
  snapshotFcrStrength = null,
  growthModelStrength = null,
  spreadsheetPersistence = null,
  sampleReliabilityScore = null,
  mfiDetectionStrength = null,
  mfiHasMissingFish = false,
  showDetails = false,
  compact = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Check if we have any signals to work with
  const hasAnySignals = snapshotFcrStrength || growthModelStrength || spreadsheetPersistence !== null || mfiDetectionStrength;
  
  // If no signals at all, show explanation
  if (!hasAnySignals) {
    return (
      <MissingDataExplanation
        fieldId="stockIntegrity"
        conditionKey="noModuleOutputs"
        fieldName="Stock Integrity Score"
        compact={compact}
      />
    );
  }

  // Calculate the score from existing signals
  const result = useMemo(() => {
    return calculateStockIntegrityScore({
      snapshotFcrStrength,
      growthModelStrength,
      spreadsheetPersistence,
      sampleReliabilityScore,
      mfiDetectionStrength,
      mfiHasMissingFish,
    });
  }, [snapshotFcrStrength, growthModelStrength, spreadsheetPersistence, sampleReliabilityScore, mfiDetectionStrength, mfiHasMissingFish]);

  // Color classes based on level
  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      badge: 'bg-green-500',
      icon: 'text-green-600',
    },
    yellow: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      badge: 'bg-amber-500',
      icon: 'text-amber-600',
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      badge: 'bg-red-500',
      icon: 'text-red-600',
    },
  };

  const colors = colorClasses[result.color];

  // Compact layout for inline display
  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.bg} ${colors.border} border`}
        data-testid="stock-integrity-score-compact"
      >
        <span className={`w-2.5 h-2.5 rounded-full ${colors.badge}`}></span>
        <span className={`text-xs font-medium ${colors.text}`}>
          Stock Integrity: {result.level}
        </span>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-lg ${colors.bg} ${colors.border} border p-3 ${showDetails ? '' : 'cursor-pointer'}`}
      onClick={showDetails ? undefined : () => setIsExpanded(!isExpanded)}
      data-testid="stock-integrity-score"
    >
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className={`w-4 h-4 ${colors.icon}`} />
          <span className="text-xs font-semibold text-gray-600">Stock Integrity Score</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Traffic Light Indicator */}
          <span className={`w-3 h-3 rounded-full ${colors.badge}`}></span>
          <span className={`text-sm font-bold ${colors.text}`}>{result.score}/8</span>
          {!showDetails && (
            isExpanded 
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expandable Details */}
      {(showDetails || isExpanded) && result.breakdown.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-1">Score Breakdown:</p>
          <div className="space-y-1">
            {result.breakdown.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{item.source}</span>
                <span className={`font-medium ${item.points > 0 ? 'text-gray-800' : 'text-green-600'}`}>
                  {item.points > 0 ? `+${item.points}` : item.points} ({item.label})
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between text-xs font-semibold pt-1 border-t border-gray-100">
              <span className="text-gray-700">Total Score</span>
              <span className={colors.text}>{result.score}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Stock Integrity Score for Growth vs Model page
 * Extracts signals from missingFishIntelligence data
 */
export const GrowthModelStockIntegrity = ({ missingFishIntelligence, compact = false }) => {
  if (!missingFishIntelligence?.analyticalSignals) return null;

  const signals = missingFishIntelligence.analyticalSignals;
  const ssot = missingFishIntelligence.ssot;
  
  return (
    <StockIntegrityScore
      growthModelStrength={signals.signalStrength?.strength || null}
      spreadsheetPersistence={signals.streak?.maxStreak || null}
      sampleReliabilityScore={signals.reliability?.score || null}
      mfiDetectionStrength={ssot?.detectionStrength || null}
      mfiHasMissingFish={ssot?.hasMissingFish || false}
      compact={compact}
    />
  );
};

/**
 * Stock Integrity Score for Spreadsheet Upload page
 * Extracts signals from missingFishIntelligence data
 */
export const SpreadsheetStockIntegrity = ({ missingFishIntelligence, overriddenOutput = null, compact = false }) => {
  if (!missingFishIntelligence?.analyticalSignals) return null;

  const signals = missingFishIntelligence.analyticalSignals;
  const ssot = missingFishIntelligence.ssot;
  
  // ★ SSOT Override: When conflict exists, use overridden values
  const hasConflict = overriddenOutput?.hasConflict || ssot?.hasConflict;
  
  return (
    <StockIntegrityScore
      growthModelStrength={signals.signalStrength?.strength || null}
      spreadsheetPersistence={signals.streak?.maxStreak || null}
      sampleReliabilityScore={signals.reliability?.score || null}
      mfiDetectionStrength={hasConflict ? 'Conflicting' : (ssot?.detectionStrength || null)}
      mfiHasMissingFish={hasConflict ? false : (ssot?.hasMissingFish || false)}
      compact={compact}
    />
  );
};

/**
 * Stock Integrity Score for Snapshot Analysis page
 * Uses FCR deviation signal strength
 */
export const SnapshotStockIntegrity = ({ fcrDeviationAnalysis, compact = false }) => {
  if (!fcrDeviationAnalysis) return null;
  
  return (
    <StockIntegrityScore
      snapshotFcrStrength={fcrDeviationAnalysis.signalStrength || null}
      compact={compact}
    />
  );
};

export default StockIntegrityScore;
